import React, { useState, useEffect, useRef } from 'react';
import { MapPin, X, Loader, Check } from 'lucide-react';
import { SAVE_ADDRESS_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { MovedLocation } from '../Utils/useLocationFlow';
import './LocationToast.css';

interface LocationToastProps {
  movedLocation: MovedLocation;
  onConfirm: () => Promise<void>;
  onDismiss: () => void;
  onLocationSaved?: () => void;
}

const AUTO_DISMISS_MS = 10000;

const LocationToast: React.FC<LocationToastProps> = ({
  movedLocation,
  onConfirm,
  onDismiss,
  onLocationSaved,
}) => {
  const [phase, setPhase] = useState<'prompt' | 'save' | 'saving' | 'done'>('prompt');
  const [label, setLabel] = useState<'Home' | 'Work' | 'Other'>('Home');
  const [saveError, setSaveError] = useState('');
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (phase !== 'prompt') return;
    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (pct === 0) {
        clearInterval(intervalRef.current!);
        onDismiss();
      }
    }, 100);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [phase, onDismiss]);

  const handleYes = async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    await onConfirm();
    setPhase('save');
  };

  const handleSaveAddress = async () => {
    setPhase('saving');
    setSaveError('');
    try {
      const body = {
        label,
        address_line: movedLocation.address,
        city: movedLocation.city,
        state: '',
        pincode: '',
        latitude: movedLocation.lat,
        longitude: movedLocation.lon,
        is_default: false,
      };
      const res = await authenticatedFetch(SAVE_ADDRESS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setPhase('done');
        onLocationSaved?.();
        setTimeout(onDismiss, 1500);
      } else {
        setSaveError(json.message || 'Could not save address.');
        setPhase('save');
      }
    } catch {
      setSaveError('Network error. Please try again.');
      setPhase('save');
    }
  };

  const cityLabel = movedLocation.city || 'a new location';

  return (
    <div className="loc-toast">
      <div className="loc-toast-inner">
        {phase === 'prompt' && (
          <>
            <div className="loc-toast-icon">
              <MapPin size={16} />
            </div>
            <div className="loc-toast-body">
              <p className="loc-toast-text">
                You seem to be near <strong>{cityLabel}</strong>. Deliver here?
              </p>
              <div className="loc-toast-progress">
                <div className="loc-toast-progress-bar" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="loc-toast-actions">
              <button className="loc-toast-btn loc-toast-btn--yes" onClick={handleYes}>
                Yes, update
              </button>
              <button className="loc-toast-btn loc-toast-btn--no" onClick={onDismiss}>
                No thanks
              </button>
            </div>
            <button className="loc-toast-close" onClick={onDismiss} aria-label="Dismiss">
              <X size={14} />
            </button>
          </>
        )}

        {phase === 'save' && (
          <>
            <div className="loc-toast-icon">
              <MapPin size={16} />
            </div>
            <div className="loc-toast-body">
              <p className="loc-toast-text">Save <strong>{cityLabel}</strong> as an address?</p>
              <div className="loc-toast-label-row">
                {(['Home', 'Work', 'Other'] as const).map(l => (
                  <button
                    key={l}
                    className={`loc-toast-label-btn${label === l ? ' active' : ''}`}
                    onClick={() => setLabel(l)}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {saveError && <p className="loc-toast-error">{saveError}</p>}
            </div>
            <div className="loc-toast-actions">
              <button className="loc-toast-btn loc-toast-btn--yes" onClick={handleSaveAddress}>
                Save
              </button>
              <button className="loc-toast-btn loc-toast-btn--no" onClick={onDismiss}>
                Skip
              </button>
            </div>
          </>
        )}

        {phase === 'saving' && (
          <div className="loc-toast-body loc-toast-body--center">
            <Loader size={16} className="loc-toast-spinner" />
            <span>Saving address...</span>
          </div>
        )}

        {phase === 'done' && (
          <div className="loc-toast-body loc-toast-body--center">
            <Check size={16} className="loc-toast-check" />
            <span>Address saved!</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationToast;
